import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { business as bizApi } from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";
import PwaInstallBanner from "@/components/PwaInstallBanner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Quotes from "@/pages/Quotes";
import QuoteEditor from "@/pages/QuoteEditor";
import QuoteDetail from "@/pages/QuoteDetail";
import PublicQuote from "@/pages/PublicQuote";
import Settings from "@/pages/Settings";
import NotificationsPage from "@/pages/Notifications";
import Templates from "@/pages/Templates";
import DashboardLayout from "@/components/DashboardLayout";

function ProtectedShell() {
  const { user, loading } = useAuth();
  const [checkedBiz, setCheckedBiz] = useState(false);
  const [hasBiz, setHasBiz] = useState(true);

  useEffect(() => {
    if (!user) return;
    bizApi.get()
      .then(({ data }) => setHasBiz(Boolean(data?.onboarded)))
      .catch(() => setHasBiz(false))
      .finally(() => setCheckedBiz(true));
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!checkedBiz) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Loading…</div>;
  if (!hasBiz) return <Navigate to="/onboarding" replace />;
  return <DashboardLayout />;
}

function OnboardingGuard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Onboarding />;
}

function RouterInner() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/q/:publicId" element={<PublicQuote />} />
      <Route path="/onboarding" element={<OnboardingGuard />} />

      <Route element={<ProtectedShell />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/quotes" element={<Quotes />} />
        <Route path="/quotes/new" element={<QuoteEditor />} />
        <Route path="/quotes/:id" element={<QuoteDetail />} />
        <Route path="/quotes/:id/edit" element={<QuoteEditor />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/settings" element={<Settings />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <RouterInner />
          <PwaInstallBanner />
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
